import React from 'react';
import { Button } from '@/components/ui/button';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import OptionsLoadingState from '../../components/options/OptionsLoadingState';
import { OptionsOnboardingCard } from '../../components/options/OptionsOnboardingCard';
import { OptionsDashboardControls } from '../../components/options/OptionsDashboardControls';
import { OptionsDashboardRangeControls } from '../../components/options/OptionsDashboardRangeControls';
import { OptionsDashboardAccountSummaryCard } from '../../components/options/OptionsDashboardAccountSummaryCard';
import { OptionsDashboardTradingPerformanceCard } from '../../components/options/OptionsDashboardTradingPerformanceCard';
import { OptionsOpenOperationsCard } from '../../components/options/OptionsOpenOperationsCard';
import { OptionsMarginCard } from '../../components/options/OptionsMarginCard';
import { BtcOutlookTeaser } from '../../components/options/BtcOutlookTeaser';
import { useOptionsDashboardData } from '../../hooks/useOptionsDashboardData';
import {
  formatCompactNumber,
} from '../../components/options/optionsDashboardUtils';
import {
  buildDashboardAccountSummaryModel,
  buildDashboardTradingPerformanceModel,
  getDashboardOpenOperationsCount,
} from '../../components/options/optionsDashboardFinancialUtils';
import { isPreviewRuntime } from '../../lib/runtime';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export default function OptionsHome() {
  const { isDemoMode } = useOptionsMode();
  const { t } = useOptionsLocale();
  const {
    accountData,
    currentCapitalPeriodsData,
    capitalPeriodsData,
    performanceData,
    historyData,
    loading,
    loadedMode,
    criticalError,
    syncing,
    rangeFetching,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    handleApplyCustomRange,
    handleSync,
    reloadDashboard,
  } = useOptionsDashboardData();

  const connected = Boolean(accountData?.connected);
  const modeResolved = loadedMode === (isDemoMode ? 'demo' : 'real');
  const showLoadingState = loading || !modeResolved;
  const showConnectionError = !showLoadingState && criticalError && !connected && !isDemoMode;
  const showDashboard = !showConnectionError && (isDemoMode || connected);
  const showTestnetBadge = isPreviewRuntime() && accountData?.environment === 'testnet';
  const accountSummary = buildDashboardAccountSummaryModel({ accountData, performanceData, historyData, selectedCurrency, t });
  const tradingPerformance = buildDashboardTradingPerformanceModel({
    accountData,
    performanceData,
    currentCapitalPeriodsData,
    capitalPeriodsData,
    historyData,
    selectedCurrency,
    t,
    selectedRange,
    customRange,
  });
  const openOperationsCount = getDashboardOpenOperationsCount({ accountData });

  return (
    <OptionsPageShell
      title={t('dashboard.title')}
      description={t('dashboard.description')}
      eyebrow={t('dashboard.eyebrow')}
      hideHeader
    >
      {showLoadingState ? (
        <OptionsLoadingState
          title={t('common.syncingNow')}
          description={t('dashboard.description')}
          testId="options-dashboard-loading-state"
        />
      ) : showConnectionError ? (
        <div className="rounded-[28px] border border-white/10 bg-white/[0.03] px-6 py-10 text-center" data-testid="options-dashboard-connection-error">
          <p className="text-lg font-semibold text-white" data-testid="options-dashboard-connection-error-title">{t('dashboard.connectionErrorTitle')}</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-white/60" data-testid="options-dashboard-connection-error-body">{t('dashboard.connectionErrorBody')}</p>
          <Button
            type="button"
            onClick={reloadDashboard}
            className="mt-6 rounded-full border border-white/10 bg-white/[0.06] text-white/80 hover:bg-white/[0.1]"
            data-testid="options-dashboard-connection-error-retry"
          >
            {t('connection.retryInitialSync')}
          </Button>
        </div>
      ) : !showDashboard ? (
        <OptionsOnboardingCard
          title={t('onboarding.title')}
          description={t('onboarding.description')}
          connectLabel={t('common.connectDeribit')}
          securityTitle={t('onboarding.securityTitle')}
          securityBody={t('onboarding.securityBody')}
          securityDetails={t('onboarding.securityDetails')}
        />
      ) : (
        <div className="space-y-6" data-testid="options-dashboard-root">
          <OptionsDashboardControls
            onSync={handleSync}
            syncing={syncing}
            environment={showTestnetBadge ? 'testnet' : accountData?.environment}
            lastSyncAt={accountData?.last_sync_at || accountData?.synced_at || accountData?.updated_at || null}
            showManageConnection={!isDemoMode && accountData?.can_manage_connection !== false}
            showSyncButton={!isDemoMode && accountData?.can_sync !== false}
          />

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr),300px]" data-testid="options-dashboard-top-grid">
            <OptionsDashboardAccountSummaryCard
              model={accountSummary}
              selectedCurrency={selectedCurrency}
              onCurrencyChange={setSelectedCurrency}
            />

            <div className="space-y-4">
              <OptionsOpenOperationsCard count={formatCompactNumber(openOperationsCount)} />
              <OptionsMarginCard accountData={accountData} loading={loading} selectedCurrency={selectedCurrency} />
              <BtcOutlookTeaser />
            </div>
          </div>

          <OptionsDashboardTradingPerformanceCard
            model={tradingPerformance}
            periodControls={
              <OptionsDashboardRangeControls
                selectedRange={selectedRange}
                onRangeChange={setSelectedRange}
                customRange={customRange}
                onCustomRangeChange={setCustomRange}
                onApplyCustomRange={handleApplyCustomRange}
                isFetching={rangeFetching}
              />
            }
          />

        </div>
      )}
    </OptionsPageShell>
  );
}